import * as L from "leaflet";

import {cs}        from "@Common/react/chain-services";
import {fragments} from "@Common/react/fragments";
import {Invoke}    from "@Common/react/invoke";
import {OnMounted} from "@Common/react/on-mounted";
import {Static2}   from "@Common/react/static-2";
import {UseState}  from "@Common/react/use-state";
import {isDevQuan} from "@Common/ui-components/debug/is-dev-quan";
import {Watch}     from "@Common/react/watch";
import {equalDeep} from "@Common/utils/objects";

export const LeafletMap = ({center, onMouseMove, onZoomEnd=undefined, renderItems=undefined, isSidebarClosed=true, xdQaPrefix, next}) => cs(
    ["domRef", (_, next) => Static2({next})],
    ["mapRef", (_, next) => Static2({next})],
    ["initialized", (_, next) => UseState({next})],
    ["activeElementKeys", (_, next) => UseState({next})],
    ["renderedElements", (_, next) => UseState({next})],
    ["prevZoom", (_, next) => UseState({initValue: 4, next})],
    ({domRef, mapRef, initialized, activeElementKeys, renderedElements, prevZoom}) => fragments(
        next({
            ref: domRef.set,
            cleanMap: ({elements}) => {
                const activeKeys = elements?.map(s => s.elementKey);
                activeElementKeys.onChange(activeKeys);

                if (renderedElements.value?.some(p => !activeKeys?.some(elementKey => p.elementKey === elementKey))) {
                    renderedElements.value?.filter(p => !activeKeys?.some(elementKey => elementKey === p.elementKey)).map(p => mapRef.get().removeLayer(p.ref.get()));
                    renderedElements.change((_) => _?.filter(p => activeKeys?.some(elementKey => elementKey === p.elementKey)));
                }
            },
            cleanItems: (itemRefs) => itemRefs.map(itemRef => itemRef.get() && mapRef.get().removeLayer(itemRef.get())),
            invalidateSize: () => mapRef.get()?.invalidateSize(),
            renderPolygon: ({shape: {elementKey, vertexList, color="#249AF3", opacity}, onMouseOver, onClick, onMouseOut}) => initialized.value && cs(
                ["polygonRef", (_, next) => Static2({next})],
                ({polygonRef}) => fragments(
                    Watch({
                        value: opacity,
                        onChanged: () => {
                            polygonRef.get().setStyle({fillOpacity: opacity});
                        },
                    }),
                    Watch({
                        value: color,
                        onChanged: () => {
                            polygonRef.get().setStyle({color});
                        }
                    }),
                    Invoke({
                        props: {onMouseOver, onClick, onMouseOut},
                        action: ({getLatestProps}) => {
                            const polygon = L
                                .polygon(vertexList, {color, fillOpacity: opacity})
                                .addTo(mapRef.get());

                            const polygonElement = polygon.getElement();
                            polygonElement.setAttribute("xd-qa-name", `${xdQaPrefix} ${elementKey} Polygon`);
                            polygonElement.setAttribute("xd-qa-type", "checkbox");

                            polygonRef.set(polygon);

                            if (onClick) {
                                polygon.on("click", (e) => {
                                    const {onClick} = getLatestProps();
                                    onClick(e);
                                });
                            }

                            if (onMouseOver) {
                                polygon.on("mouseover", (e) => {
                                    const {onMouseOver} = getLatestProps();
                                    onMouseOver(e);
                                });
                            }
                            if (onMouseOut) {
                                polygon.on("mouseout", onMouseOut);
                            }

                            renderedElements.change((_) => [
                                ..._ ?? [],
                                {
                                    elementKey,
                                    ref: polygonRef,
                                    type: "polygon",
                                },
                            ]);
                        },
                    }),
                ),
            ),
            createMarker: ({marker: {elementKey, coords, opacity}, onMouseOver, onClick=undefined, onMouseOut, markerType}) => initialized.value && cs(
                ["markerRef", (_, next) => Static2({next})],
                ({markerRef}) => fragments(
                    Watch({
                        value: opacity,
                        onChanged: () => {
                            if(markerType === "building") {
                                markerRef.get().setIcon(
                                    L.divIcon({
                                        className: 'building-icon',
                                        html: `<i class="fa fa-building" style="opacity: ${opacity};" xd-qa-type="icon" xd-qa-name="${xdQaPrefix} ${elementKey} Marker"></i>`,
                                        iconSize: [30, 30]
                                    })
                                );
                            } else if (markerType === "point") {
                                markerRef.get().setStyle({fillOpacity: opacity});
                            }
                        },
                    }),
                    Invoke({
                        props: {onMouseOver, onClick, onMouseOut},
                        action: ({getLatestProps}) => {
                            let marker;

                            if (markerType === "building") {
                                const buildingIcon = L.divIcon({
                                    className: 'building-icon',
                                    html: `<i class="fa fa-building" style="opacity: ${opacity};" xd-qa-type="icon" xd-qa-name="${xdQaPrefix} ${elementKey} Marker"></i>`,
                                    iconSize: [30, 30]
                                });
                                
                                marker = L.marker(coords, {icon: buildingIcon});
                            } else if (markerType === "point") {
                                marker = L.circleMarker(coords, {color: "#249AF3", fillOpacity: opacity, radius: 7});

                                marker.on('add', function() {
                                    const markerElement = marker.getElement();
                                    markerElement.setAttribute("xd-qa-name", `${xdQaPrefix} ${elementKey} Marker`);
                                    markerElement.setAttribute("xd-qa-type", "icon");
                                })
                            }

                            markerRef.set(marker);

                            if (onClick) {
                                marker.on("click", (e) => {
                                    const {onClick} = getLatestProps();
                                    onClick(e);
                                });
                            }

                            if (onMouseOver) {
                                marker.on("mouseover", (e) => {
                                    const {onMouseOver} = getLatestProps();
                                    onMouseOver(e);
                                });
                            }
                            if (onMouseOut) {
                                marker.on("mouseout", onMouseOut);
                            }

                            renderedElements.change((_) => [
                                ..._ ?? [],
                                {
                                    elementKey,
                                    ref: markerRef,
                                    type: "marker",
                                },
                            ]);
                        },
                    }),
                )
            ),
            createCustomIcon: ({elementKey, coords, icon, onMouseOver, onMouseOut}) => initialized.value && cs(
                ["iconRef", (_, next) => Static2({next})],
                ({iconRef}) => fragments(
                    Invoke({
                        props: {onMouseOver, onMouseOut},
                        action: ({getLatestProps}) => {
                            const pillIcon = L.divIcon(icon)
                            const pill = L
                                .marker([coords['lat'], coords['lng']], {icon: pillIcon});
                            
                            if (onMouseOver) {
                                pill.on("mouseover", (e) => {
                                    const {onMouseOver} = getLatestProps();
                                    onMouseOver(e);
                                });
                            }
                            if (onMouseOut) {
                                pill.on("mouseout", onMouseOut);
                            }

                            iconRef.set(pill);

                            renderedElements.change((_) => [
                                ..._ ?? [],
                                {
                                    elementKey,
                                    ref: iconRef,
                                    type: "icon",
                                },
                            ])
                        }
                    })
                )
            )
        }),
        Watch({
            value: renderedElements.value,
            compare: (e1, e2) => equalDeep(e1?.map(e => e.elementKey), e2?.map(e => e.elementKey)),
            onChanged: () => {
                if(renderedElements.value?.length) {
                    let bounds = L.latLngBounds();
                    renderedElements.value.forEach(e => {
                        if(e.type == "polygon"){
                          bounds.extend(e.ref.get().getBounds());
                        } else if(e.type == "marker"){
                          bounds.extend(e.ref.get().getLatLng());
                        }
                    });

                    const map = mapRef.get();
                    const padding = isSidebarClosed ? {
                        padding: [50, 50]
                    } : {
                        paddingTopLeft: [600, 50],
                        paddingBottomRight: [50, 50]
                    };
                    map.fitBounds(bounds, padding);
                    
                    const zoom = map.getZoom();
                    // If zoom changed sizes, zoomend function will handle rendering. Don't double render
                    if (renderItems && (prevZoom.value === zoom || !onZoomEnd)) {
                        renderItems({map, zoom, renderedElements: renderedElements.value});
                    }

                    prevZoom.onChange(zoom);
                }
            }
        }),
        OnMounted({
            props: {onZoomEnd, prevZoom, renderedElements},
            action: ({getLatestProps}) => {
                const dom = domRef.get();
                const map = L
                    .map(dom)
                    .setView(center, 4)
                ;

                map.doubleClickZoom.disable();
                L
                    .tileLayer('https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.{ext}?api_key=749dfe8e-b945-49a3-a685-5df2aa670810', {
                        maxZoom: 19,
                        attribution: '&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                        ext: 'png'
                    })
                    .addTo(map)
                ;

                if (onMouseMove) {
                    map.on("mousemove", onMouseMove);
                }

                if (onZoomEnd) {
                    map.on("zoomend", () => {
                        const {onZoomEnd, renderedElements, prevZoom} = getLatestProps();
                        onZoomEnd({map, prevZoom, renderedElements: renderedElements.value});
                    });
                }

                mapRef.set(map);

                const zoomInButton = document.querySelector('.leaflet-control-zoom-in');
                if (zoomInButton) {
                    zoomInButton.setAttribute('xd-qa-name', `${xdQaPrefix} Zoom In`);
                    zoomInButton.setAttribute('xd-qa-type', 'button');
                }

                const zoomOutButton = document.querySelector('.leaflet-control-zoom-out');
                if (zoomOutButton) {
                    zoomOutButton.setAttribute('xd-qa-name', `${xdQaPrefix} Zoom Out`);
                    zoomOutButton.setAttribute('xd-qa-type', 'button');
                }

                initialized.onChange(true);

                isDevQuan() && (
                    dom.scrollIntoView()
                )
            },
        })
    )
);